import React, { useEffect, useCallback, useState} from 'react';
import EnvelopesComponent from 'components/instruments/EnvelopesComponent.js';
import FilterSection from 'components/instruments/FilterSection.js';
import FMOperatorControl from 'components/instruments/FMOperatorControl.js';
import RackComponent from 'components/effects/rack/RackComponent.js';
import PresetsExplorer from 'components/installation/PresetsExplorer.js';
import {connect} from 'react-redux';
import MultiSynthComponent from 'components/instruments/MultiSynthComponent.js';
import {ENVELOPE_OPTIONS} from 'components/instruments/constants.js';

const SynthDocumentation = (props) => { 
    let [track, setTrack] = useState(undefined);
    let [selectedOscillator, setSelectedOscillator] = useState(0);
    let {sequencerEngine} = props;

    useEffect(() => {
        sequencerEngine.newSubSequence();
        sequencerEngine.switchToSubSequence(
            props.sequencerEngine.subSequences[props.sequencerEngine.subSequences.length - 1].id);
        let track = sequencerEngine.addMultiSynthTrack();
        setTrack(track);
        return () => {
            sequencerEngine.deleteSubSequence();
        };
    }, [setTrack]);

    return (
        <div className="synth-documentation">
          <p>
            Zequencer has a 4-operator FM synth that allows you to synthesize
            a wide range of sounds. The add a new <b>Synth</b> track, click on the <b>Synth</b> button
            on the sidebar. 
          </p>
          <p>
            The added Synth track will look like this:
          </p>
          {track ?
           <MultiSynthComponent
             instrument={track.instrument}
             track={track}
             sequencerEngine={sequencerEngine}
           /> : ''}
          <div className="two-column">
            <div className="text-column">
              <p>
                When you add a <b>Synth</b> track, the sidebar will show you 
                you all the different presets you can select.
              </p>
              <p>
                You can search through presets by name and tag.
              </p>
              <p>
                Clicking on an option in the list to load the sound into the selected <b>Synth</b> track.
              </p>
            </div>
            <div className="text-column">
              {track ? <PresetsExplorer content={props.content || {contentHash: ''}} track={track} /> : ''}
            </div>
          </div>
          <div className="two-column">
            <div className="text-column">
              <p className="sub-header">
                Operators
              </p>
              <p>
                The synth consists of 4 operators, named C, A, B1, B2.
              </p>
              <p>
                An operator is an oscillator, with FM-enabled modulation, built-in.
                Think of it as a simple tone-generator. 
              </p>
              <p>
                The operator named C, refers to the Carrier Operator. This is the
                main oscillator that controls the pitch of the whole Synthesizer.
              </p>
              <p>
                The other operators-- A, B1, B2-- are called Modulator Operators.
                They modulate the frequency of other oscillators, according to their
                Algorithm and Harmonicity ratios.
              </p>
            </div>
            <div className="text-column rack-demo">
              {track ? <RackComponent
                         track={track}
                         scrollContainer={undefined}
                         setSelectedOscillator={(osc) => setSelectedOscillator(
                             Math.max(0, track.instrument.oscillators.indexOf(osc)))}
                         sequencerEngine={sequencerEngine}
                         effect={track.instrument}/> : ''}
            </div>
          </div>
          <div className="two-column">
            <div className="text-column">
              <p className="sub-header">
                Harmonicity Ratio
              </p>
              <p>
                Each operator contains a <b>Harmonicity</b> and <b>Modulation Index</b>.
              </p>
              <p>
                The <b>Harmonicity Ratio</b> controls the pitch of the oscillator,
                according to a ratio to the Carrier Oscillator’s frequency.
              </p>
              <p>
                For example a <b>harmonicity</b> of 2 means the pitch will be double
                (or an octave above), the pitch Carrier Oscillator’s pitch.
              </p>
              <p className="sub-header">
                Modulation Index
              </p>
              <p>
                Each operator’s <b>modulation index</b> controls the amount of modulation that
                operator will reach, at the peak of the operator’s envelope.
              </p>
              <p>
                Both <b>Harmonicity</b> and <b>Modulation Index</b> can be controlled via a 2
                dimensional control, with the 4 operators shown as circles on an 2D plane.
              </p>
              <p>
                The x-axis refers to <b>Harmonicity</b> and the y-axis refers to <b>Modulation Index</b>.
              </p>
            </div>
            <div className="text-column">
              <p className="sub-header">
                Algorithm
              </p>
              <p>
                The <b>Synth's Algorithm</b> describes how the different
                operators are connected to each other.
              </p>
              <p>
                There are 8 algorithms, that can be selected via
                the algorithm dropdown.
              </p>
              <p>
                Try changing the algorithm above to see how the configuration
                changes, visually.
              </p>
              <p>
                Each algorithm has a different timbre, and can wildly
                change the sound, when changed.
              </p>
              <p className="sub-header">
                X/Y Mix
              </p>
              <p>
                Each <b>operator</b> is fed into either other <b>operators</b>, or 2
                different outputs that are crossfed. 
              </p>
              <p>
                These outputs, called <b>X</b> and <b>Y</b>, once mixed are outputted through
                the <b>Filter</b> and <b>Amplitude Envelope</b>, and then finally through
                the rest of your track
              </p>
              <p>
                Balance between the <b>X</b> and <b>Y</b> outputs is controlled by the <b>Mix</b>
                parameter, shown as a slider.
              </p>
            </div>
          </div>
          <div className="two-column">
            <div className="text-column">
              <p className="sub-header">
                Operator Control
              </p>
              <p>
                Each <b>operator</b> has a few other parameters that control how they sound.
              </p>
              <p>
                When an <b>operator</b> is selected, parameters specific to
                that <b>operator</b> are shown. They are shown here, to the right.
              </p>
              <p>
                The <b>waveform type</b> controls what kind of <b>waveform</b> the oscillator,
                and can be selected. By default, it uses a <b>Sine wave</b>. You can
                use the <b>dropdown</b> to change the <b>waveform</b>.
              </p>
              <p>
                The <b>partials</b> parameter controls the harmonics of the waveform.
                When set to 0, the full waveform is used. When 1 it uses just
                the 1st harmonic of the waveform. When set to 2, it uses just the
                2nd harmonic of the waveform, and so on.
              </p>
              <p>
                The <b>Oscillator Level</b> parameter controls the amplitude of the operator.
                Essentiallly how loud it is.
              </p>
              <p>
                The <b>Detune</b> parameter controls how detuned (in cents) the operator will be.
              </p>
              <p>
                The <b>Frequency</b> parameter controls what the base note of the Synth
                will be. If it’s set to C3, then when you play C3 the synth will trigger the note at C3.
              </p>
            </div>
            <div className="text-column fm-operator-demo">
              <p className="sub-header">
                Toggling FM On/Off
              </p>
              <p>
                You may want to free an operator’s frequency from being
                controlled by the <b>Carrier Oscillator</b>. To do this, click
                on the <b>FM</b> checkbox.
              </p>
              <p>
                When untoggled, you can set the <b>Frequency</b> parameter for
                that specific <b>operator</b>. The frequency will no longer be a product of
                the carrier oscillator’s frequency and this operator’s harmonicity.
              </p>
            </div>
          </div>
          <div className="two-column">
            <div className="text-column">
              <p className="sub-header">
                Filter
              </p>
              <p>
                Once signal passes through the X/Y mixer, it goes into the
                Filter. This lets you subtract off unwanted frequencies from your tone.
              </p>
              <p>
                By default, the <b>Filter Mode</b> is in Lowpass, meaning all
                frequencies above the <b>cutoff frequency</b> are shaved off.
              </p>
              <p>
                The <b>cutoff frequency</b> parameter controls this.
              </p>
              <p>
                The <b>Q</b> parameter controls how resonant (or wet-sounding) the
                filter is. Basically the frequencys around the <b>cutoff frequency</b> will
                get more exaggerated, the higher you go. 
              </p>
            </div>
            <div className="text-column">
              {track ? <FilterSection
                         sequencerEngine={sequencerEngine}
                         instrument={track.instrument}
                       /> : ''}
            </div>
          </div>
          <div className="two-column">
            <div className="text-column">
              <p className="sub-header">
                Envelopes
              </p>
              <p>
                The <b>Synth</b> has several built-in <b>envelopes</b> that control how
                various parameters behave, upon triggering the <b>Synth</b>.
                For more information on envelopes, see the <b>Envelopes</b> documentation page.
              </p>
              <p>
                The first one that is shown is the <b>Amplitude Envelope</b> which
                controls the overall amplitude, or loudness, of the output of the synth.
              </p>
              <p>
                The <b>modulation envelopes</b> control the amplitude of each
                operator’s signal. This has huge implications for the
                overall tone of the synth. Each <b>operator</b> can collectively add
                to the character in subtle ways by carefully choosing
                the various <b>ADSR</b> parameters here.
              </p>
              <p>
                The <b>filter envelope</b> controls the cutoff frequency of the filter. 
              </p>
              <p>
                The <b>pitch envelope</b> controls the frequency of the entire Synth.
                This can be extremely useful when trying to synthesize percussive sounds
              </p>
              <p>
                Both the <b>filter envelope</b> and <b>pitch envelope</b> have an 
                <b>Env Depth</b> parameter that controls the level at which the envelope will
                reach at it’s peak, at the end of the Attack stage (and before the decay stage).
              </p>
              <p>
                The level reached at the <b>sustain</b> stage will be a product of both the
                <b>env depth</b> and the <b>sustain</b> parameter.
              </p>
            </div>
            <div classNaame="text-column">
              {track ? 
                      <EnvelopesComponent
                        envelopeOptions={ENVELOPE_OPTIONS}
                        sequencerEngine={sequencerEngine}
                        instrument={track.instrument}/> : ''}
 
            </div>
          </div>
        </div>
    );
}

const mapStateToProps = (state, props) => {
  return {
      content: props.sequencerEngine.tracks &&
          props.sequencerEngine.tracks[0] &&
          props.sequencerEngine.tracks[0].preset
          
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(SynthDocumentation);
